import axios from "axios";
import type { AxiosRequestConfig } from "axios";

type PortalAxiosRequestConfig = AxiosRequestConfig & {
  _skipAuthRefresh?: boolean;
};

const portalTokenKey = "agentPortalToken";
const portalActorKey = "agentPortalActor";

/** Stores the portal-only short-lived access token and actor payload for this browser tab. */
export const setPortalSession = (token: string, actor?: unknown) => {
  sessionStorage.setItem(portalTokenKey, token);
  if (actor) sessionStorage.setItem(portalActorKey, JSON.stringify(actor));
};

/** Returns the portal-only access token without reading CRM local storage. */
export const getPortalAccessToken = () => sessionStorage.getItem(portalTokenKey);

/** Returns the cached portal actor when one was stored during login. */
export const getPortalActor = () => {
  const actor = sessionStorage.getItem(portalActorKey);
  if (!actor) return null;
  try {
    return JSON.parse(actor) as unknown;
  } catch {
    return null;
  }
};

/** Removes portal-only browser state without touching the CRM session. */
export const clearPortalSession = () => {
  sessionStorage.removeItem(portalTokenKey);
  sessionStorage.removeItem(portalActorKey);
};

/** Refreshes the portal access token through the portal-specific HTTP-only refresh cookie. */
export const refreshPortalSession = async () => {
  const response = await axios.post("/api/agent-portal/auth/refresh", {}, {
    withCredentials: true,
    _skipAuthRefresh: true,
  } as PortalAxiosRequestConfig);
  setPortalSession(response.data.token);
  return response.data.token as string;
};

/** Calls a protected portal endpoint with the portal token only. */
export const portalRequest = async <T>(
  config: Pick<AxiosRequestConfig, "url" | "method" | "data" | "params" | "responseType">,
) => {
  const token = getPortalAccessToken();
  return axios.request<T>({
    ...config,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    _skipAuthRefresh: true,
  } as PortalAxiosRequestConfig);
};

/** Revokes the portal refresh session without modifying the CRM cookie/session. */
export const logoutPortalSession = async () => {
  try {
    await axios.post("/api/agent-portal/auth/logout", {}, {
      withCredentials: true,
      _skipAuthRefresh: true,
    } as PortalAxiosRequestConfig);
  } finally {
    clearPortalSession();
  }
};
